import {
  Drawer,
  Box,
  VStack,
  Text,
  HStack,
  Slider,
  Button,
  Separator,
  IconButton,
  RadioGroup,
} from "@chakra-ui/react";
import { MdGraphicEq } from "react-icons/md";
import { FiX } from "react-icons/fi";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";

import Categories from "./Categories";
import ColorRadios from "./ColorRadios";
import SizeSelect from "./Sizes";
import SelectC from "../../theme/SelectC";

const MotionBox = motion(Box);

export default function MobileFiltersDrawer({
  isOpen,
  onClose,
  filters,
  backendFilters,
  handleFilterChange,
  clear,
  refresh,
  filtersStatus,
  activeCount = 0,
}) {
  const colorsData = backendFilters?.colors || [];
  const sizesData = backendFilters?.sizes || [];
  const sortOptions = [
    { label: "Featured", value: "featured" },
    { label: "Price: Low to High", value: "price_low" },
    { label: "Price: High to Low", value: "price_high" },
    { label: "Newest", value: "newest" },
  ];

  // Local sort state, independent of filters.sort_by
  const [localSort, setLocalSort] = useState("featured");

  // Sync localSort when drawer opens or filters.sort_by changes
  useEffect(() => {
    if (filters?.sort_by) {
      setLocalSort(filters.sort_by);
    } else {
      setLocalSort("featured");
    }
  }, [filters?.sort_by, isOpen]);

  const handleOpenChange = (details) => {
    if (!details.open) {
      onClose();
    }
  };

  const handleApply = () => {
    // Only here we push sort to parent
    if (localSort) {
      handleFilterChange("sort_by", localSort);
    }
    refresh();
    onClose();
  };

  return (
    <Drawer.Root
      open={isOpen}
      onOpenChange={handleOpenChange}
      placement="bottom"
      size="full"
    >
      <Drawer.Backdrop />
      <Drawer.Positioner>
        <Drawer.Content borderTopRadius="2xl" bg="white" p={0}>
          <MotionBox
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
          >
            {/* Drag handle */}
            <Box
              as={motion.div}
              drag="y"
              dragConstraints={{ top: 0, bottom: 120 }}
              onDragEnd={(_, info) => {
                if (info.offset.y > 80) onClose();
              }}
              w="60px"
              h="5px"
              bg="gray.300"
              borderRadius="full"
              mx="auto"
              mt={2}
              mb={3}
            />

            {/* Header */}
            <Box px={4} pb={3} pt={2} position="relative" minH="45px">
              <HStack justify="space-between" align="center">
                <Text fontSize="lg" fontWeight="700">
                  Filters
                </Text>

                {activeCount > 0 && (
                  <Box
                    fontSize="xs"
                    px={2}
                    py={1}
                    rounded="full"
                    bg="black"
                    color="white"
                  >
                    {activeCount} applied
                  </Box>
                )}

                <Box
                  as="div"
                  fontSize="22px"
                  fontWeight="bold"
                  cursor="pointer"
                  userSelect="none"
                  bg="white"
                  px="8px"
                  py="2px"
                  borderRadius="md"
                  _active={{ opacity: 0.6 }}
                  onClick={onClose}
                >
                  <FiX size={20} />
                </Box>
              </HStack>
            </Box>

            <Separator />

            {/* Body */}
            <Box px={4} pt={4} pb={20} maxH="70vh" overflowY="auto">
              <VStack align="stretch" spacing={6}>
                {/* Categories */}
                <Box>
                  <Text fontSize="md" fontWeight="semibold" mb={2}>
                    Categories
                  </Text>
                  <Categories
                    filters={filters}
                    handleFilterChange={handleFilterChange}
                    backendFilters={backendFilters}
                    filtersStatus={filtersStatus}
                  />
                </Box>

                <Separator />

                {/* Color */}
                <Box>
                  <Text fontSize="md" fontWeight="semibold" mb={2}>
                    Color
                  </Text>
                  <ColorRadios
                    selectedValue={filters.colors}
                    colorItems={colorsData}
                    onChange={(color) => handleFilterChange("colors", color)}
                  />
                </Box>

                <Separator />

                {/* Size */}
                <Box>
                  <Text fontSize="md" fontWeight="semibold" mb={2}>
                    Size
                  </Text>
                  <SizeSelect
                    selectedValue={filters.sizes}
                    sizeItems={sizesData}
                    onChange={(size) => handleFilterChange("sizes", size)}
                  />
                </Box>

                <Separator />

                {/* Price */}
                <Box>
                  <Text fontSize="md" fontWeight="semibold" mb={2}>
                    Price
                  </Text>
                  <Slider.Root
                    min={0}
                    max={5000}
                    defaultValue={[filters.min_price, filters.max_price]}
                    onValueChangeEnd={(e) =>
                      handleFilterChange("price", e.value)
                    }
                  >
                    <Slider.Control>
                      <Slider.Track>
                        <Slider.Range bg="tomato" />
                      </Slider.Track>
                      <Slider.Thumb index={0}>
                        <Box as={MdGraphicEq} color="tomato" />
                      </Slider.Thumb>
                      <Slider.Thumb index={1}>
                        <Box as={MdGraphicEq} color="tomato" />
                      </Slider.Thumb>
                    </Slider.Control>
                  </Slider.Root>
                  <Text mt={2} fontSize="xs" color="gray.600">
                    ₹ {filters.min_price} – ₹ {filters.max_price}
                  </Text>
                </Box>

                <Separator />

                {/* Sort */}
                <Box>
                  <Text fontSize="md" fontWeight="semibold" mb={2}>
                    Sort By
                  </Text>

                  <RadioGroup.Root
                    value={localSort}
                    onValueChange={(details) => {
                      const value = details.value;
                      setLocalSort(value); // only local change
                    }}
                    orientation="vertical"
                    gap="3"
                  >
                    {sortOptions.map((option) => (
                      <RadioGroup.Item
                        key={option.value}
                        value={option.value}
                        cursor="pointer"
                        display="flex"
                        alignItems="center"
                        gap="2"
                        px={1}
                        py={1}
                      >
                        <RadioGroup.ItemHiddenInput />

                        <RadioGroup.ItemIndicator
                          boxSize="4"
                          borderWidth="2px"
                          borderColor="gray.700"
                          rounded="full"
                          _checked={{
                            bg: "gray.900",
                            borderColor: "gray.900",
                          }}
                        />

                        <RadioGroup.ItemText fontSize="sm">
                          {option.label}
                        </RadioGroup.ItemText>
                      </RadioGroup.Item>
                    ))}
                  </RadioGroup.Root>
                </Box>
              </VStack>
            </Box>

            {/* Footer buttons */}
            <Box
              position="absolute"
              bottom={0}
              left={0}
              right={0}
              px={4}
              py={3}
              bg="white"
              borderTopWidth="1px"
              borderColor="gray.200"
            >
              <HStack spacing={3}>
                <Button
                  flex="1"
                  variant="outline"
                  onClick={() => {
                    clear();
                    // reset local sort to default when clearing
                    setLocalSort("featured");
                  }}
                >
                  Clear
                </Button>
                <Button
                  flex="1"
                  bg="black"
                  color="white"
                  onClick={handleApply}
                >
                  Apply
                </Button>
              </HStack>
            </Box>
          </MotionBox>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  );
}
